import { type DescMethod } from '@bufbuild/protobuf';
import { type Account } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { ClusterService } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { type User } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { type QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { memoize } from 'micro-memoize';
import { AccountMissingError, isAccountMissingError } from '@/api/errors';
import {
  checkRpcPermissions,
  hasPermissionsWithoutRpc,
  type PermissionsCombinator,
  type PermissionWithoutRpc,
} from '@/api/permissions';
import { type accountService } from '@/api/services/account';
import { iamService } from '@/api/services/iam';
import { createStatsigUpdateUserQuery } from '@/api/services/statsig';
import { safeAsync } from '@/helpers/errors/safeAsync';
import { sortedUserAccounts } from '@/routes/_authenticated.hooks';
import { resolveAuthenticatedUser } from '@/routes/_authenticated.resolvers';
import { type AccountId } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { type NonEmptyArray } from '@/utils/types';

/**
 * Returns the current selected account ID, derived from the URL param or search params.
 */
export const resolveSelectedAccountId = ({
  params,
  search,
}: {
  params: { accountId?: string };
  search: { account_id?: string };
  // Both sources are validated as a guid before they get here — `accountIdUrlParam` for the path
  // param, `authenticatedSearchSchema` for the search one — so only the brand is missing.
}) => (params.accountId ?? search.account_id) as AccountId | undefined;

/**
 * Ensures that AccountId is valid and returns the corresponding account.
 * If AccountId is invalid or missing in the list of accounts, it throws an `AccountMissingError`.
 * If AccountId is not provided, it returns the first account from the sorted list of accounts,
 * which is guaranteed to always be available since the user must have at least one account
 * if this function is called. The sorting is based on the user's email and default account ID
 * or date of creation, ensuring that the default account is prioritized.
 *
 * Use `isAccountMissingError(error)` to check for this error type.
 */
export const ensureSelectedAccount = ({
  accounts,
  user,
  accountId,
}: {
  accounts: Account[];
  user: User;
  accountId?: AccountId;
}): Account => {
  if (accounts.length === 0) {
    throw new AccountMissingError();
  }
  const fallbackAccount = sortedUserAccounts(accounts, user)[0];
  if (accountId) {
    const account = accounts.find((account: Account) => account.id === accountId);
    if (account) {
      return account;
    }
    throw new AccountMissingError(accountId, fallbackAccount.id);
  }
  return fallbackAccount;
};

const memoizedEnsureSelectedAccount = memoize(ensureSelectedAccount);

export type ResolveUserAccountContext = {
  queryClient: QueryClient;
  getAuthenticatedUserQuery: ReturnType<typeof iamService.getAuthenticatedUserQuery>;
  listAccountsQuery: ReturnType<typeof accountService.listAccountsQuery>;
};

/**
 * Use instead of `useAccount()` when you need to resolve user data outside of a React component.
 */
export async function resolveAccount({
  accountId,
  ...context
}: { accountId?: AccountId } & ResolveUserAccountContext): Promise<Account> {
  const { items: accounts } = await context.queryClient.ensureQueryData(context.listAccountsQuery);
  const user = await resolveAuthenticatedUser(context);

  return memoizedEnsureSelectedAccount({ accounts, user, accountId });
}

type ResolveAccountUserPermissionArgs = {
  queryClient: QueryClient;
  accountId: string;
};

/**
 * The account's effective permissions, as the set every check works on — the same shape
 * `useEffectivePermissionsForAccountId` hands components and `getCachedAccountPermissions` pulls out of
 * the cache. Fetches when the `$accountId` loader has not primed the cache already.
 */
export async function resolveAccountPermissions({
  queryClient,
  accountId,
}: ResolveAccountUserPermissionArgs): Promise<Set<string>> {
  const { permissions } = await queryClient.ensureQueryData(iamService.listEffectivePermissionsQuery({ accountId }));

  return new Set(permissions.map(({ value }) => value));
}

/**
 * Use instead of `useRpcPermissions()` when you need to check RPC permissions outside of a React
 * component (route `beforeLoad`/`loader`, imperative helpers). This is the default way to check a
 * permission: it covers every one some RPC declares, which is all of them but `PERMISSIONS_WITHOUT_RPC`.
 */
export async function resolveAccountHasRpcPermissions(
  methods: NonEmptyArray<DescMethod>,
  { combinator, ...options }: { combinator?: PermissionsCombinator } & ResolveAccountUserPermissionArgs,
): Promise<boolean> {
  return checkRpcPermissions(methods, await resolveAccountPermissions(options), combinator).matches;
}

/**
 * The exception to `resolveAccountHasRpcPermissions`, and the only way to check one of the permissions
 * no RPC declares outside a React component — components call `hasPermissionsWithoutRpc` directly on
 * the set they already hold.
 *
 * `PermissionWithoutRpc` is the whole set this accepts: anything an RPC declares is a type error here,
 * because it has a method to be checked against instead.
 */
export async function resolveAccountHasPermissionsWithoutRpc(
  requiredPermissions: NonEmptyArray<PermissionWithoutRpc>,
  options: ResolveAccountUserPermissionArgs,
): Promise<boolean> {
  return hasPermissionsWithoutRpc(requiredPermissions, await resolveAccountPermissions(options));
}

/**
 * Use instead of `usePrivilegeAccessControl()` when you need to resolve privileges outside of a React
 * component. Privileges are account-level flags (e.g. `PREMIUM_TIER`), not RPC permissions — for those
 * use `resolveAccountHasRpcPermissions`.
 */
export async function resolveAccountHasPrivileges(
  requiredPrivileges: string[],
  options: {
    accountId: AccountId;
  } & ResolveUserAccountContext,
) {
  const { privileges } = await resolveAccount(options);

  return requiredPrivileges.every((p) => privileges.includes(p));
}

export async function resolveClusterRouteEntrypoint({
  accountId,
  queryClient,
  search,
}: {
  accountId: AccountId;
  search?: object;
} & ResolveUserAccountContext): Promise<never> {
  const canReadClusters = await resolveAccountHasRpcPermissions([ClusterService.method.listClusters], {
    queryClient,
    accountId,
  });

  throw redirect({
    to: canReadClusters ? '/accounts/$accountId/clusters' : '/get-started',
    params: { accountId },
    replace: true,
    search,
  });
}

export async function resolveStatsigUpdateUser({
  accountId,
  ...context
}: {
  accountId?: AccountId;
} & ResolveUserAccountContext) {
  const [user, [accountError, account]] = await Promise.all([
    resolveAuthenticatedUser(context),
    safeAsync(resolveAccount({ accountId, ...context })),
  ]);

  if (accountError && !isAccountMissingError(accountError)) {
    throw accountError;
  }

  return createStatsigUpdateUserQuery({ user, account });
}
